import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CategoryAdminItem,
  CategoryRef,
  CreateCategoryInput,
  CreateTagInput,
  TagAdminItem,
  TagRef,
  UpdateCategoryInput,
  UpdateTagInput,
} from '@news-cms/shared';
import { ApiError, api } from './api';
import { toast } from '@/components/ui/toaster';

interface ListResp<T> {
  items: T[];
}

export interface TagListResult {
  items: TagAdminItem[];
  pagination: { page: number; perPage: number; total: number };
}

// List returns admin shape (CategoryAdminItem); TaxonomyPicker treats it as
// the lighter CategoryRef superset — it ignores extra fields.
export const categoriesListQueryOptions = (q?: string) =>
  queryOptions({
    queryKey: ['taxonomies', 'categories', { q: q ?? '' }] as const,
    queryFn: () =>
      api<ListResp<CategoryAdminItem>>(
        `/api/admin/categories${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      ).then((d) => d.items),
    staleTime: 30_000,
  });

export const tagsListQueryOptions = (q?: string, page = 1) =>
  queryOptions({
    queryKey: ['taxonomies', 'tags', { q: q ?? '', page }] as const,
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (page > 1) params.set('page', String(page));
      const qs = params.toString();
      return api<TagListResult>(`/api/admin/tags${qs ? `?${qs}` : ''}`);
    },
    staleTime: 30_000,
  });

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCategoryInput) => {
      const data = await api<{ category: CategoryRef }>('/api/admin/categories', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return data.category;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taxonomies', 'categories'] });
    },
    onError: (e) => toast.error(taxonomyErrorMessage(e)),
  });
}

export function useCreateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTagInput) => {
      const data = await api<{ tag: TagRef }>('/api/admin/tags', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return data.tag;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taxonomies', 'tags'] });
    },
    onError: (e) => toast.error(taxonomyErrorMessage(e)),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateCategoryInput }) => {
      const data = await api<{ category: CategoryRef }>(`/api/admin/categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      return data.category;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taxonomies', 'categories'] });
      toast.success('แก้ไขหมวดเรียบร้อย');
    },
    onError: (e) => toast.error(taxonomyErrorMessage(e)),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<{ ok: true }>(`/api/admin/categories/${id}`, { method: 'DELETE' });
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taxonomies', 'categories'] });
      toast.success('ลบหมวดเรียบร้อย');
    },
    onError: (e) => toast.error(taxonomyErrorMessage(e)),
  });
}

/**
 * Swap a category with the neighbor above/below within its parent
 * group. Backend returns `{ moved: false }` when target is already
 * at the edge — silent no-op (no toast), since clicking ↑ on the
 * topmost row is an expected gesture, not a failure.
 */
export function useReorderCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: 'up' | 'down' }) => {
      return api<{ moved: boolean }>(`/api/admin/categories/${id}/reorder`, {
        method: 'POST',
        body: JSON.stringify({ direction }),
      });
    },
    onSuccess: (data) => {
      // Always invalidate so the renumbered sortOrder lands in the
      // next read — even on a no-op (moved:false) the list shape
      // is identical, but we want react-query to treat the mutation
      // as completed.
      void qc.invalidateQueries({ queryKey: ['taxonomies', 'categories'] });
      if (!data.moved) return; // edge case — silent
    },
    onError: (e) => toast.error(taxonomyErrorMessage(e)),
  });
}

/**
 * Bulk reorder within one sibling group — caller supplies the full
 * ordered id list for a single parent (parentId=null = top-level) and
 * backend renumbers them as i*10 atomically. Drag handler computes
 * the new sequence via arrayMove; cross-parent drops are filtered
 * client-side before this is called (backend would 400 anyway).
 */
export function useReorderCategoriesBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { parentId: string | null; orderedIds: string[] }) => {
      return api<{ updated: number }>('/api/admin/categories/_reorder', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taxonomies', 'categories'] });
    },
    onError: (e) => toast.error(taxonomyErrorMessage(e)),
  });
}

export function useUpdateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateTagInput }) => {
      const data = await api<{ tag: TagRef }>(`/api/admin/tags/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      return data.tag;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taxonomies', 'tags'] });
      toast.success('แก้ไขแท็กเรียบร้อย');
    },
    onError: (e) => toast.error(taxonomyErrorMessage(e)),
  });
}

export function useDeleteTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<{ ok: true }>(`/api/admin/tags/${id}`, { method: 'DELETE' });
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taxonomies', 'tags'] });
      toast.success('ลบแท็กเรียบร้อย');
    },
    onError: (e) => toast.error(taxonomyErrorMessage(e)),
  });
}

/**
 * Atomically rewrite tag sort_order from an ordered id list. Both the
 * ↑/↓ buttons and the drag-and-drop handle feed into this — the caller
 * computes the new order locally (via arrayMove for drag, or by
 * swapping adjacent ids for the buttons) and posts the full sequence.
 * Backend renumbers as i*10 in one db.batch. Silent no-op (empty
 * array → backend returns updated:0) — UI shouldn't trigger that.
 */
export function useReorderTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      return api<{ updated: number }>('/api/admin/tags/reorder', {
        method: 'POST',
        body: JSON.stringify({ orderedIds }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taxonomies', 'tags'] });
    },
    onError: (e) => toast.error(taxonomyErrorMessage(e)),
  });
}

/**
 * Merge N source tags into a single target. Source tags are deleted
 * after their post_tags refs are moved (with ON CONFLICT skip in case
 * a post already carried both). Returns `{ mergedCount, targetId }`
 * so the caller can surface a friendly toast.
 */
export function useMergeTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { fromIds: string[]; toId: string }) => {
      const data = await api<{ mergedCount: number; targetId: string }>(`/api/admin/tags/_merge`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return data;
    },
    onSuccess: ({ mergedCount }) => {
      void qc.invalidateQueries({ queryKey: ['taxonomies', 'tags'] });
      // Posts cache also stale — categories renamed/merged change list
      // rendering (chip names + filter targets).
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      toast.success(`รวม ${mergedCount} แท็กเรียบร้อย`);
    },
    onError: (e) => toast.error(taxonomyErrorMessage(e)),
  });
}

export function taxonomyErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เกิดข้อผิดพลาด';
  switch (e.code) {
    case 'SLUG_CONFLICT':
      return 'slug นี้ถูกใช้แล้ว';
    case 'FORBIDDEN':
      return 'คุณไม่มีสิทธิ์จัดการ';
    case 'VALIDATION_ERROR':
      return 'ข้อมูลไม่ถูกต้อง';
    case 'CATEGORY_NOT_FOUND':
      return 'ไม่พบหมวดที่ต้องการ';
    case 'TAG_NOT_FOUND':
      return 'ไม่พบแท็กที่ต้องการ';
    case 'INVALID_PARENT':
      return 'parent ไม่ถูกต้อง';
    default:
      return e.message || 'เกิดข้อผิดพลาด';
  }
}
